/**
 * /communications — cross-project communications feed (Patch 2).
 *
 * Surfaces every logged communication across the entire portfolio (and any
 * master-anchored comms). Threads are grouped: one row per thread with the
 * most recent message shown. The "▶ N" badge indicates a multi-message
 * thread; single-message threads render as one row identical to non-threaded
 * comms.
 *
 * The filter strip mirrors what's on the per-project Documents tab plus a
 * counterparty filter and an "Anchor scope" picker (All / Project / Master).
 *
 * Filters serialise to URL search params (deferred to Patch 3 polish).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import {
  ArrowDown,
  Calendar,
  Filter,
  Mail,
  MessageCircle,
  MessageSquare,
  NotebookPen,
  Paperclip,
  Phone,
  Plus,
  Search,
  Send,
} from 'lucide-react';
import { formatRelative } from '@procurement/shared';
import { PageHeader } from '@/components/ui/page-header';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/form-field';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Select } from '@/components/ui/select';
import { EmptyState } from '@/components/ui/empty-state';
import { SortableHeader } from '@/components/ui/sortable-header';
import { AttachmentDetailDrawer } from '@/components/attachments/detail-drawer';
import { LogCommunicationModal } from '@/components/attachments/log-communication-modal';
import { useAttachmentsList } from '@/lib/attachments-queries';
import type {
  Attachment,
  AttachmentListFilters,
  CommDirection,
  CommType,
  CommsSortKey,
} from '@/lib/attachments-queries';
import { useProjectsList } from '@/lib/visibility-queries';
import { useClientsListAll, useSuppliersListAll } from '@/lib/masters-queries';
import { usePageTitle } from '@/lib/use-page-title';

const COMM_TYPE_LABELS: Record<CommType, string> = {
  PHONE_CALL: 'Phone call',
  MEETING: 'Meeting',
  LETTER_RECEIVED: 'Letter received',
  LETTER_SENT: 'Letter sent',
  EMAIL: 'Email',
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
  INTERNAL_NOTE: 'Internal note',
};

function commIcon(t: CommType | null) {
  switch (t) {
    case 'PHONE_CALL':
      return Phone;
    case 'MEETING':
      return Calendar;
    case 'LETTER_RECEIVED':
      return ArrowDown;
    case 'LETTER_SENT':
      return Send;
    case 'EMAIL':
      return Mail;
    case 'WHATSAPP':
    case 'SMS':
      return MessageSquare;
    case 'INTERNAL_NOTE':
      return NotebookPen;
    default:
      return MessageCircle;
  }
}

type DateWindow = '7' | '30' | '90' | 'ALL';

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export function CommunicationsPage() {
  usePageTitle('Communications');

  // Patch 4 — accept tag + open query params for deep-linking from global
  // search and elsewhere. Both seed local state once on mount; subsequent
  // user interactions own the truth.
  const urlSearch = useSearch({ strict: false }) as {
    tag?: string;
    open?: string;
  };

  const [dateWindow, setDateWindow] = useState<DateWindow>('30');
  const [direction, setDirection] = useState<CommDirection | ''>('');
  const [commType, setCommType] = useState<CommType | ''>('');
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState(urlSearch.tag ?? '');
  const [anchorScope, setAnchorScope] = useState<'PROJECT' | 'MASTER' | ''>('');
  const [filterProject, setFilterProject] = useState<string>('');
  const [filterClient, setFilterClient] = useState<string>('');
  const [filterSupplier, setFilterSupplier] = useState<string>('');
  const [showLog, setShowLog] = useState(false);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<string | null>(
    urlSearch.open ?? null,
  );
  // Patch 5 — sortable columns. Default occurredAt desc (matches server).
  const [sortBy, setSortBy] = useState<CommsSortKey>('occurredAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleSort = (key: CommsSortKey): void => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  // If the URL changes (e.g. the user navigates from one search result to
  // another while staying on this page), pick up the new ids/tags. Effect
  // intentionally writes only when the URL value changes — local edits to
  // the filter aren't clobbered by re-rendering.
  useEffect(() => {
    if (urlSearch.tag !== undefined) setTag(urlSearch.tag);
  }, [urlSearch.tag]);
  useEffect(() => {
    if (urlSearch.open !== undefined) setSelectedAttachmentId(urlSearch.open);
  }, [urlSearch.open]);

  const projectsQ = useProjectsList({ pageSize: 200, excludeClosed: true });
  const clientsQ = useClientsListAll();
  const suppliersQ = useSuppliersListAll();

  const filters = useMemo<AttachmentListFilters>(() => {
    const f: AttachmentListFilters = {
      kind: 'COMMUNICATION',
      pageSize: 100,
      sortBy,
      sortDir,
    };
    if (dateWindow !== 'ALL') f.dateFrom = isoDaysAgo(Number(dateWindow));
    if (direction) f.direction = direction;
    if (commType) f.commType = commType;
    if (search.trim()) f.search = search.trim();
    if (tag.trim()) f.tag = tag.trim().toLowerCase();
    if (anchorScope) f.anchorScope = anchorScope;
    if (filterProject) f.projectId = filterProject;
    if (filterClient) f.clientId = filterClient;
    if (filterSupplier) f.supplierId = filterSupplier;
    return f;
  }, [dateWindow, direction, commType, search, tag, anchorScope, filterProject, filterClient, filterSupplier, sortBy, sortDir]);

  const listQ = useAttachmentsList(filters);

  // Client-side thread grouping: one row per thread_id, surfacing the most
  // recent message. Server-side grouping is in the schema (threadGroup flag)
  // but for the cross-project surface, fetching with kind=COMMUNICATION and
  // grouping in-memory is fine at 100 items per page.
  const grouped = useMemo(() => {
    const items = listQ.data?.items ?? [];
    const byThread = new Map<string, { latest: Attachment; count: number }>();
    for (const a of items) {
      const tid = a.threadId ?? a.id;
      const existing = byThread.get(tid);
      if (!existing) {
        byThread.set(tid, { latest: a, count: 1 });
      } else {
        existing.count += 1;
        // most recent by occurred_at (then created_at)
        const aDate = a.occurredAt ?? a.createdAt;
        const eDate = existing.latest.occurredAt ?? existing.latest.createdAt;
        if (aDate > eDate) existing.latest = a;
      }
    }
    return [...byThread.values()].sort((a, b) => {
      // Patch 5 — apply same sort key as the server query so the in-memory
      // grouping preserves it. Default occurredAt; otherwise compare on
      // the chosen string field.
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortBy) {
        case 'subject':
          return (a.latest.subject ?? '').localeCompare(b.latest.subject ?? '') * dir;
        case 'counterpartyName': {
          const aN = a.latest.counterpartyName ?? '';
          const bN = b.latest.counterpartyName ?? '';
          // NULLS LAST regardless of direction (matches server)
          if (aN === '' && bN !== '') return 1;
          if (bN === '' && aN !== '') return -1;
          return aN.localeCompare(bN) * dir;
        }
        case 'occurredAt':
        default: {
          const aDate = a.latest.occurredAt ?? a.latest.createdAt;
          const bDate = b.latest.occurredAt ?? b.latest.createdAt;
          return aDate.localeCompare(bDate) * dir;
        }
      }
    });
  }, [listQ.data, sortBy, sortDir]);

  const projectOptions = useMemo(
    () =>
      (projectsQ.data?.items ?? []).map((p) => ({
        id: p.projectId,
        label: `${p.projectNumber} — ${p.projectTitle}`,
      })),
    [projectsQ.data],
  );
  const clientOptions = useMemo(
    () => (clientsQ.data ?? []).map((c) => ({ id: c.id, label: c.name })),
    [clientsQ.data],
  );
  const supplierOptions = useMemo(
    () => (suppliersQ.data ?? []).map((s) => ({ id: s.id, label: s.name })),
    [suppliersQ.data],
  );

  const activeFilterCount = [
    dateWindow !== '30' ? 1 : 0,
    direction ? 1 : 0,
    commType ? 1 : 0,
    search.trim() ? 1 : 0,
    tag.trim() ? 1 : 0,
    anchorScope ? 1 : 0,
    filterProject ? 1 : 0,
    filterClient ? 1 : 0,
    filterSupplier ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const clearAll = () => {
    setDateWindow('30');
    setDirection('');
    setCommType('');
    setSearch('');
    setTag('');
    setAnchorScope('');
    setFilterProject('');
    setFilterClient('');
    setFilterSupplier('');
  };

  return (
    <>
      <PageHeader
        title="Communications"
        subtitle="Phone calls, meetings, letters, and emails across all projects."
        actions={
          <Button variant="primary" onClick={() => setShowLog(true)}>
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
            Log communication
          </Button>
        }
      />

      <Card className="mb-3 p-3">
        {/*
         * Patch P18 — filter layout restructured. Search box up top
         * (primary filter, full-width so it's obvious), all dropdowns
         * left-aligned below in natural flow. Previous layout had search
         * and tag inputs right-aligned with `ml-auto`, creating awkward
         * gaps between the dropdown row and the orphaned right-side
         * controls (visible in user's screenshot — "filters look bad").
         */}
        <div className="relative mb-2 flex items-center">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-tertiary"
            strokeWidth={1.5}
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search subjects, notes, contacts"
            className="h-9 w-full pl-8 text-sm"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={dateWindow}
            onChange={(e) => setDateWindow(e.target.value as DateWindow)}
            className="w-[140px]"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="ALL">All time</option>
          </Select>
          <Select
            value={direction}
            onChange={(e) => setDirection(e.target.value as CommDirection | '')}
            className="w-[140px]"
          >
            <option value="">All directions</option>
            <option value="INBOUND">Inbound</option>
            <option value="OUTBOUND">Outbound</option>
            <option value="INTERNAL">Internal</option>
          </Select>
          <Select
            value={commType}
            onChange={(e) => setCommType(e.target.value as CommType | '')}
            className="w-[150px]"
          >
            <option value="">All types</option>
            {Object.entries(COMM_TYPE_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </Select>
          <Select
            value={anchorScope}
            onChange={(e) =>
              setAnchorScope(e.target.value as 'PROJECT' | 'MASTER' | '')
            }
            className="w-[140px]"
          >
            <option value="">All anchors</option>
            <option value="PROJECT">Project</option>
            <option value="MASTER">Master entity</option>
          </Select>
          {anchorScope !== 'MASTER' ? (
            <SearchableSelect
              items={projectOptions.map((o) => ({ value: o.id, label: o.label }))}
              value={filterProject || null}
              onChange={(v) => setFilterProject(v ?? '')}
              placeholder="Any project"
              clearable
              className="w-[220px]"
            />
          ) : null}
          {anchorScope !== 'PROJECT' ? (
            <>
              <SearchableSelect
                items={clientOptions.map((o) => ({ value: o.id, label: o.label }))}
                value={filterClient || null}
                onChange={(v) => setFilterClient(v ?? '')}
                placeholder="Any client"
                clearable
                className="w-[180px]"
              />
              <SearchableSelect
                items={supplierOptions.map((o) => ({ value: o.id, label: o.label }))}
                value={filterSupplier || null}
                onChange={(v) => setFilterSupplier(v ?? '')}
                placeholder="Any supplier"
                clearable
                className="w-[180px]"
              />
            </>
          ) : null}
          <Input
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            placeholder="Tag"
            className="w-32 text-sm"
          />
        </div>
        {activeFilterCount > 0 ? (
          <div className="mt-2 flex items-center gap-2 text-[11px] text-secondary">
            <Filter className="h-3 w-3" strokeWidth={1.5} />
            <span>{activeFilterCount} active filter{activeFilterCount === 1 ? '' : 's'}</span>
            <button
              type="button"
              onClick={clearAll}
              className="ml-auto text-accent-strong hover:underline"
            >
              Clear all
            </button>
          </div>
        ) : null}
      </Card>

      <Card className="p-0">
        {listQ.isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner className="h-5 w-5 text-tertiary" />
          </div>
        ) : listQ.isError ? (
          <div className="p-4">
            <Alert tone="danger">Could not load communications. Try again.</Alert>
          </div>
        ) : grouped.length === 0 ? (
          activeFilterCount > 0 ? (
            <EmptyState
              title="No communications match your filters"
              body="Adjust filters or clear them to see more."
              action={
                <Button variant="secondary" size="compact" onClick={clearAll}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No communications logged yet"
              body="Log your first phone call, meeting, or letter to start the trail."
              action={
                <Button variant="primary" size="compact" onClick={() => setShowLog(true)}>
                  Log communication
                </Button>
              }
            />
          )
        ) : (
          <>
            {/* Patch 5 — sort bar. List view (not a table) so we surface
                the sort as a button row instead of clickable column heads. */}
            <div className="flex items-center gap-3 border-b border-border-default px-4 py-2 text-xs text-secondary">
              <span className="font-semibold uppercase tracking-wide">Sort</span>
              <SortableHeader
                label="Date"
                active={sortBy === 'occurredAt'}
                direction={sortDir}
                onToggle={() => toggleSort('occurredAt')}
              />
              <SortableHeader
                label="Subject"
                active={sortBy === 'subject'}
                direction={sortDir}
                onToggle={() => toggleSort('subject')}
              />
              <SortableHeader
                label="Counterparty"
                active={sortBy === 'counterpartyName'}
                direction={sortDir}
                onToggle={() => toggleSort('counterpartyName')}
              />
            </div>
            <ul className="divide-y divide-border-default">
              {grouped.map((g) => (
                <CommunicationRow
                  key={g.latest.id}
                  latest={g.latest}
                  threadSize={g.count}
                  onOpen={() => setSelectedAttachmentId(g.latest.id)}
                />
              ))}
            </ul>
          </>
        )}
      </Card>

      {showLog ? (
        <LogCommunicationModal
          open={showLog}
          onClose={() => setShowLog(false)}
          // Cross-project — let the user pick a project OR a master.
          projectId={null}
          projects={projectOptions}
          clients={clientOptions}
          suppliers={supplierOptions}
        />
      ) : null}

      <AttachmentDetailDrawer
        attachmentId={selectedAttachmentId}
        onClose={() => setSelectedAttachmentId(null)}
      />
    </>
  );
}

function CommunicationRow({
  latest,
  threadSize,
  onOpen,
}: {
  latest: Attachment;
  threadSize: number;
  onOpen: () => void;
}) {
  const Icon = commIcon(latest.commType);
  const arrow =
    latest.direction === 'INBOUND' ? '↙' : latest.direction === 'OUTBOUND' ? '↗' : '—';
  const anchorBreadcrumb = describeAnchor(latest);

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="grid w-full grid-cols-[28px_220px_1fr_180px_24px_120px_60px] items-center gap-2 px-3 py-2 text-left text-xs hover:bg-subtle"
      >
        <div className="flex items-center gap-1 text-tertiary">
          <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
          <span className="text-secondary">{arrow}</span>
        </div>
        <div className="truncate text-secondary">{latest.counterpartyName ?? '—'}</div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-primary">{latest.subject}</span>
            {threadSize > 1 ? (
              <span className="inline-flex items-center rounded-sm border border-accent/40 bg-accent/10 px-1 text-[10px] font-medium text-accent-strong">
                ▶ {threadSize}
              </span>
            ) : null}
          </div>
          {latest.notes ? (
            <div className="truncate text-secondary">{latest.notes}</div>
          ) : null}
        </div>
        <div className="truncate text-tertiary">{anchorBreadcrumb}</div>
        <div className="flex justify-center">
          {/* Patch 2: attachment indicator on a per-message basis; aggregated
            * across the thread in Patch 3 polish. */}
          <Paperclip className="h-3 w-3 text-tertiary opacity-50" strokeWidth={1.5} />
        </div>
        <div className="text-secondary tabular-nums">
          {formatRelative(latest.occurredAt ?? latest.createdAt)}
        </div>
        <div className="truncate text-tertiary">{initials(latest.createdByDisplayName)}</div>
      </button>
      {latest.tags.length > 0 ? (
        <div className="mb-2 ml-12 flex flex-wrap gap-1">
          {latest.tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center rounded-sm bg-accent/10 px-1.5 py-0.5 text-[10px] text-accent-strong"
            >
              #{t}
            </span>
          ))}
        </div>
      ) : null}
    </li>
  );
}

function describeAnchor(a: Attachment): string {
  if (a.bankGuaranteeId) return 'BG (project)';
  if (a.orderBatchId) return 'Batch';
  if (a.projectItemId) return 'Part';
  if (a.inspectionEventId) return 'Inspection';
  if (a.projectId) return 'Project-level';
  if (a.clientId) return 'Client';
  if (a.supplierId) return 'Supplier';
  if (a.masterDepartmentId) return 'Department';
  return '—';
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((n) => n[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
